import { withErrorBoundary } from 'app/components/error-boundary';
import { images } from 'app/design/images';
import { Box, Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { ProgressIndicator } from 'app/design/primitives/progress-indicator';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';

import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useOnboarding } from 'app/hooks/use-onboarding';
import { useOnboardingNavigation } from 'app/screens/onboarding/credentials';
import { usePersonalInformationForm } from 'app/screens/onboarding/hooks/use-personal-information-form';
import { usePersonalInformationSubmitFlow } from 'app/screens/onboarding/hooks/use-personal-information-submit-flow';
import {
  ONBOARDING_LARGE_OVERLAY_HEADER_HEIGHT,
  onboardingLargeModalHeight,
} from 'app/screens/onboarding/onboarding-large-modal-layout';
import { PersonalInformationFormFields, PhoneFieldHelpModal } from 'app/screens/onboarding/personal-information.shared';
import type { NavigationScreen } from 'app/types/navigation';
import { usePageTitle } from 'app/utils/web-helpers';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TextInput } from 'react-native';
import { KeyboardAvoidingView, Platform, View, useWindowDimensions } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();
const CONTENT_CONTAINER_STYLE = { paddingBottom: theme.spacing['spacing.general.x2'] };

const PersonalInformationScreenComponent: NavigationScreen<'personal-information'> = ({ navigation }) => {
  const { current, max } = useOnboarding();
  const { t } = useScopedTranslation();
  const { t: tOnboarding } = useTranslation('onboarding');
  const { control, handleSubmit, userInfo } = usePersonalInformationForm();
  const { isSubmitLoading, onSubmit, error } = usePersonalInformationSubmitFlow({ handleSubmit });

  const setPageTitle = usePageTitle('personal-information');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const { progressActiveColor } = useOnboardingNavigation(userInfo?.type, { headerPresentation: 'overlay' });
  const { height: windowHeight } = useWindowDimensions();
  const modalHeight = onboardingLargeModalHeight(windowHeight);
  const insets = useSafeAreaInsets();

  useNavigationOptions(navigation, { headerBackVisible: false });

  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const tSuffix =
    userInfo?.type === 'public' && t('firstNameFieldLabel-public') !== 'firstNameFieldLabel-public' ? '-public' : '';

  const refAdditionalName = useRef<TextInput>(null);
  const refLastName = useRef<TextInput>(null);
  const refPhoneNumber = useRef<TextInput>(null);

  const formFieldsProps = {
    control,
    onOpenPhoneInfo: () => setIsInfoOpen(true),
    refAdditionalName,
    refLastName,
    refPhoneNumber,
    t,
    tSuffix,
    userInfo,
  };

  const primaryProps = {
    loading: isSubmitLoading,
    onPress: () => void onSubmit(),
  };

  const phoneHelpModal = <PhoneFieldHelpModal isOpen={isInfoOpen} t={t} onClose={() => setIsInfoOpen(false)} />;

  return (
    <Column backgroundColor="text.primary" flex={1}>
      <Box bottom={0} left={0} position="absolute" right={0} top={0}>
        <PCImage cachePolicy="memory-disk" contentFit="cover" flex={1} source={images.web.personal_information} />
      </Box>
      <Column backgroundColor="text.primary" bottom={0} left={0} opacity={0.42} position="absolute" right={0} top={0} />
      <SafeAreaView edges={['top', 'left', 'right']} style={{ flex: 1 }}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
          <View
            style={{
              flex: 1,
              justifyContent: 'center',
              paddingHorizontal: theme.spacing['spacing.general.x4'],
              paddingTop: ONBOARDING_LARGE_OVERLAY_HEADER_HEIGHT,
            }}
          >
            <Column
              alignSelf="center"
              backgroundColor="surface.default"
              borderRadius="radius.xl"
              elevation={6}
              gap="spacing.general.x4"
              maxWidth={480}
              paddingHorizontal="spacing.general.x8"
              paddingTop="spacing.general.x8"
              shadowColor="black"
              shadowOffset={{ height: 4, width: 0 }}
              shadowOpacity={0.12}
              shadowRadius={16}
              style={{ height: modalHeight }}
              width="100%"
            >
              <ScrollView
                contentContainerStyle={CONTENT_CONTAINER_STYLE}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
                style={{ flex: 1 }}
              >
                <ProgressIndicator activeColor={progressActiveColor} index={current} length={max} />

                <Text
                  fontWeight="600"
                  marginBottom="spacing.general.x4"
                  marginTop="spacing.general.x8"
                  textAlign="center"
                  variant="headerXSmall"
                >
                  {t('title')}
                </Text>

                <Column gap="spacing.general.x5">
                  <PersonalInformationFormFields {...formFieldsProps} />
                </Column>
              </ScrollView>

              {error ? <RemoteError error={error} /> : null}

              <Column style={{ paddingBottom: Math.max(insets.bottom, theme.spacing['spacing.general.x4']) }}>
                <Button variant="primary" {...primaryProps}>
                  <Text color="surface.default" textAlign="center">
                    {current === max - 1 ? tOnboarding('confirm') : tOnboarding('nextStep')}
                  </Text>
                </Button>
              </Column>
            </Column>
          </View>
        </KeyboardAvoidingView>
      </SafeAreaView>
      {phoneHelpModal}
    </Column>
  );
};

export const PersonalInformationScreen: NavigationScreen<'personal-information'> = withErrorBoundary(
  PersonalInformationScreenComponent,
);
